// Sequential "Categorise them" for the Atlas Sector lens — Story #6837 Stream 9.
//
// One classify call per not-yet-categorised invention, strictly one at a time
// (concurrency 1: each call is a paid model call and the API holds one run lease
// per project), reporting progress after each. The first failure stops the run: a
// 429 shows retry_after from the BODY, anything else the server's own words. Nothing
// is retried, so a refusal can never turn into a loop of paid calls.
import { postMarketClassify } from '../../services/api'
import type { MarketClassifyResult } from '../../types/market'

export interface CategoriseDeps {
  postMarketClassify: (projectId: string) => Promise<MarketClassifyResult>
}

// Resolved at call time so screens whose tests stub services/api with an explicit
// factory can import this module without ever categorising.
const realDeps: CategoriseDeps = { postMarketClassify: (projectId) => postMarketClassify(projectId) }

export interface CategoriseResult {
  /** Calls that returned (a status of any kind, including insufficient_basis). */
  done: number
  total: number
  error: string | null
}

type HttpErr = { response?: { status?: number; data?: { error?: unknown; retry_after?: unknown } }; message?: string }

function failureText(err: unknown): string {
  const e = err as HttpErr | null | undefined
  const body = e?.response?.data ?? {}
  if (e?.response?.status === 429) {
    const ra = typeof body.retry_after === 'number' ? ` — try again in ${body.retry_after} s` : ''
    return `Too many market analyses right now${ra}.`
  }
  if (e?.response?.status === 504) return 'Categorising is taking longer than expected — reload in a minute to see what finished.'
  return typeof body.error === 'string' && body.error ? body.error : e?.message || 'Categorising stopped unexpectedly.'
}

export async function categoriseSequentially(
  projectIds: string[],
  onProgress: (done: number, total: number) => void = () => {},
  deps: CategoriseDeps = realDeps,
): Promise<CategoriseResult> {
  const total = projectIds.length
  let done = 0
  onProgress(0, total)
  for (const id of projectIds) {
    try {
      await deps.postMarketClassify(id)
    } catch (err) {
      return { done, total, error: failureText(err) }
    }
    done += 1
    onProgress(done, total)
  }
  return { done, total, error: null }
}
